import React, { useState, useEffect, useRef } from "react";
import api from "./services/api";

const GST_RATES = ["18%", "12%", "5%", "28%", "0%"];

const COMMON_HINTS = [
  "Pump Spare Part / Component",
  "Submersible Motor Spares",
  "Stainless Steel Hardware / Fastener",
  "Electrical Switchgear & Panel",
  "Rubber / Polymer Sealing Ring / Gasket",
  "Industrial Valve & Pipe Fitting",
  "Workshop Tool / Consumable",
  "Bearing & Transmission Equipment",
];

export default function HsnManager() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBatch, setActiveBatch] = useState("all");
  const [activeStatus, setActiveStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  // Autocomplete state
  const [activeSearchId, setActiveSearchId] = useState(null);
  const [masterSuggestions, setMasterSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounce = useRef(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);

  // AI loading state
  const [aiLoadingId, setAiLoadingId] = useState(null);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [notification, setNotification] = useState(null);

  // Hint Modals state
  const [hintModalItem, setHintModalItem] = useState(null);
  const [hintText, setHintText] = useState("");
  const [bulkHintModalOpen, setBulkHintModalOpen] = useState(false);
  const [bulkHintText, setBulkHintText] = useState("");

  const showNotification = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await api.get("/hsn/stats");
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching HSN stats:", err);
    }
  };

  // Fetch items
  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/hsn/items`, {
        params: {
          batch: activeBatch,
          status: activeStatus,
          search: searchTerm,
          page,
          limit,
        },
      });
      setItems(res.data.items || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
    } catch (err) {
      console.error("Error fetching HSN items:", err);
      showNotification("Failed to load items", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [activeBatch, activeStatus, page, limit]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchItems();
  };

  // Selection handlers
  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllOnPage = () => {
    const pageItemIds = items
      .filter((it) => it.status !== "SECTION_HEADER")
      .map((it) => it.id);

    const allSelected = pageItemIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageItemIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageItemIds])));
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Autocomplete search across 22,616 GST Portal codes
  const handleHsnInputChange = (itemId, val) => {
    setItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, suggested_hsn: val } : it))
    );

    setActiveSearchId(itemId);
    if (!val || val.trim().length < 2) {
      setMasterSuggestions([]);
      return;
    }

    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const res = await api.get(`/hsn/search-master`, { params: { q: val } });
        setMasterSuggestions(res.data || []);
      } catch (e) {
        console.error("Master search error:", e);
      } finally {
        setSearchLoading(false);
      }
    }, 250);
  };

  const handleSelectSuggestion = async (itemId, suggestion) => {
    try {
      await api.put(`/hsn/items/${itemId}`, {
        suggested_hsn: suggestion.hsn_code,
        suggested_desc: suggestion.hsn_description,
        status: "MATCHED_FROM_MASTER",
      });

      setItems((prev) =>
        prev.map((it) =>
          it.id === itemId
            ? {
                ...it,
                suggested_hsn: suggestion.hsn_code,
                suggested_desc: suggestion.hsn_description,
                status: "MATCHED_FROM_MASTER",
              }
            : it
        )
      );
      setActiveSearchId(null);
      setMasterSuggestions([]);
      showNotification(`HSN updated to ${suggestion.hsn_code}`);
      fetchStats();
    } catch (err) {
      console.error("Error updating item:", err);
      showNotification("Failed to update item", "error");
    }
  };

  const handleRateChange = async (itemId, newRate) => {
    try {
      await api.put(`/hsn/items/${itemId}`, { gst_rate: newRate });
      setItems((prev) =>
        prev.map((it) => (it.id === itemId ? { ...it, gst_rate: newRate } : it))
      );
      showNotification(`Rate updated to ${newRate}`);
    } catch (err) {
      console.error("Error updating rate:", err);
    }
  };

  const handleToggleApprove = async (itemId, currentApproved) => {
    try {
      const nextApproved = currentApproved ? 0 : 1;
      const nextStatus = nextApproved ? "APPROVED" : "NEEDS_REVIEW";
      await api.put(`/hsn/items/${itemId}`, {
        is_approved: nextApproved,
        status: nextStatus,
      });
      setItems((prev) =>
        prev.map((it) =>
          it.id === itemId
            ? { ...it, is_approved: nextApproved, status: nextStatus }
            : it
        )
      );
      showNotification(nextApproved ? "Item approved!" : "Approval removed");
      fetchStats();
    } catch (err) {
      console.error("Error toggling approval:", err);
    }
  };

  const handleBulkApprove = async (onlyThisBatch = false) => {
    const confirmMsg = onlyThisBatch
      ? `Approve all verified & matched items in Day ${activeBatch}?`
      : "Approve all verified & matched items across the ENTIRE system?";
    if (!window.confirm(confirmMsg)) return;

    try {
      const payload = onlyThisBatch
        ? { batch_no: activeBatch }
        : { approve_all: true };
      const res = await api.post("/hsn/bulk-approve", payload);
      showNotification(`Successfully approved ${res.data.affectedRows} items!`);
      fetchStats();
      fetchItems();
    } catch (err) {
      console.error("Error in bulk approve:", err);
      showNotification("Bulk approve failed", "error");
    }
  };

  const handleBulkApproveSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Approve all ${selectedIds.length} selected items?`)) return;

    try {
      const res = await api.post("/hsn/bulk-approve", { ids: selectedIds });
      showNotification(`Successfully approved ${res.data.affectedRows} items!`);
      setSelectedIds([]);
      fetchStats();
      fetchItems();
    } catch (err) {
      console.error("Error bulk approving selected:", err);
      showNotification("Failed to approve selected items", "error");
    }
  };

  const handleAskAi = async (itemId, customHint = null) => {
    try {
      setAiLoadingId(itemId);
      const payload = customHint !== null ? { ai_hint: customHint } : {};
      const res = await api.post(`/hsn/ai-assist/${itemId}`, payload);
      if (res.data && res.data.suggestion) {
        const { correct_hsn, gst_rate, hsn_desc } = res.data.suggestion;
        let rateStr = String(gst_rate || "18%").trim();
        if (!rateStr.includes("%")) rateStr += "%";
        const hsnStr = String(correct_hsn || "").trim();
        const descStr = String(hsn_desc || "").trim();

        await api.put(`/hsn/items/${itemId}`, {
          suggested_hsn: hsnStr,
          suggested_desc: descStr,
          gst_rate: rateStr,
          status: "NEEDS_REVIEW",
          is_approved: 0,
          in_ai_queue: 0,
          ...(customHint !== null ? { ai_hint: customHint } : {}),
        });

        setItems((prev) =>
          prev.map((it) =>
            it.id === itemId
              ? {
                  ...it,
                  suggested_hsn: hsnStr,
                  suggested_desc: descStr,
                  gst_rate: rateStr,
                  status: "NEEDS_REVIEW",
                  is_approved: 0,
                  in_ai_queue: 0,
                  ...(customHint !== null ? { ai_hint: customHint } : {}),
                }
              : it
          )
        );
        showNotification(`AI Suggested: ${hsnStr} (${rateStr})!`);
        fetchStats();
      }
    } catch (err) {
      console.error("AI error:", err);
      showNotification("AI assistance failed", "error");
    } finally {
      setAiLoadingId(null);
    }
  };

  // Hint Modal Handlers
  const openHintModal = (item) => {
    setHintModalItem(item);
    setHintText(item.ai_hint || "");
  };

  const closeHintModal = () => {
    setHintModalItem(null);
    setHintText("");
  };

  const handleSaveHintOnly = async () => {
    if (!hintModalItem) return;
    try {
      await api.put(`/hsn/items/${hintModalItem.id}`, { ai_hint: hintText });
      setItems((prev) =>
        prev.map((it) =>
          it.id === hintModalItem.id ? { ...it, ai_hint: hintText } : it
        )
      );
      showNotification("Description hint saved!");
      closeHintModal();
    } catch (err) {
      console.error("Save hint error:", err);
      showNotification("Failed to save hint", "error");
    }
  };

  const handleSaveHintAndQueue = async () => {
    if (!hintModalItem) return;
    try {
      await api.post("/hsn/queue-ai", {
        items: [{ id: hintModalItem.id, ai_hint: hintText }],
      });
      setItems((prev) =>
        prev.map((it) =>
          it.id === hintModalItem.id
            ? { ...it, ai_hint: hintText, in_ai_queue: 1 }
            : it
        )
      );
      showNotification("Item queued for AI review with your description!");
      fetchStats();
      closeHintModal();
    } catch (err) {
      console.error("Queue item error:", err);
      showNotification("Failed to queue item", "error");
    }
  };

  // Queue Multiple Selected Items
  const handleQueueSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await api.post("/hsn/queue-ai", { ids: selectedIds });
      showNotification(`Queued ${res.data.queuedCount} items for AI review!`);
      setItems((prev) =>
        prev.map((it) =>
          selectedIds.includes(it.id) ? { ...it, in_ai_queue: 1 } : it
        )
      );
      setSelectedIds([]);
      fetchStats();
    } catch (err) {
      console.error("Queue selected error:", err);
      showNotification("Failed to queue selected items", "error");
    }
  };

  // Dequeue Items
  const handleDequeue = async (idsToDequeue) => {
    try {
      await api.post("/hsn/dequeue-ai", { ids: idsToDequeue });
      setItems((prev) =>
        prev.map((it) =>
          idsToDequeue.includes(it.id) ? { ...it, in_ai_queue: 0 } : it
        )
      );
      showNotification("Removed from AI queue");
      fetchStats();
    } catch (err) {
      console.error("Dequeue error:", err);
      showNotification("Failed to dequeue", "error");
    }
  };

  const handleClearEntireQueue = async () => {
    if (!window.confirm("Remove all items from AI review queue?")) return;
    try {
      await api.post("/hsn/dequeue-ai", { all: true });
      showNotification("AI Queue cleared!");
      fetchStats();
      fetchItems();
    } catch (err) {
      console.error("Clear queue error:", err);
      showNotification("Failed to clear queue", "error");
    }
  };

  // Bulk Hint & Queue
  const handleApplyBulkHintAndQueue = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post("/hsn/queue-ai", {
        ids: selectedIds,
        ai_hint: bulkHintText,
      });
      setItems((prev) =>
        prev.map((it) =>
          selectedIds.includes(it.id)
            ? { ...it, ai_hint: bulkHintText, in_ai_queue: 1 }
            : it
        )
      );
      showNotification(
        `Added ${selectedIds.length} items to AI queue with description!`
      );
      setSelectedIds([]);
      setBulkHintModalOpen(false);
      setBulkHintText("");
      fetchStats();
    } catch (err) {
      console.error("Bulk hint error:", err);
      showNotification("Failed to apply bulk hint", "error");
    }
  };

  // Process AI Queue
  const handleRunAiQueue = async () => {
    try {
      setIsProcessingQueue(true);
      showNotification(
        "AI is analyzing queued items with your custom descriptions... Please wait.",
        "info"
      );
      const res = await api.post("/hsn/process-ai-queue");
      if (res.data && res.data.processed > 0) {
        showNotification(
          `AI successfully reviewed ${res.data.processed} items! Switched to 'Needs Review' tab to inspect and approve.`,
          "success"
        );
        setActiveStatus("NEEDS_REVIEW");
      } else {
        showNotification(res.data?.message || "No items in AI review queue", "info");
      }
      fetchStats();
      fetchItems();
    } catch (err) {
      console.error("Process AI queue error:", err);
      showNotification("Error processing AI queue", "error");
    } finally {
      setIsProcessingQueue(false);
    }
  };

  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingXml, setDownloadingXml] = useState(false);

  // Direct Tally Downloads using Axios Blob
  const downloadTallyExcel = async (batchParam = "all") => {
    try {
      setDownloadingExcel(true);
      showNotification("Generating Tally Excel file... please wait", "info");
      const res = await api.get("/hsn/export-tally-excel", {
        params: { batch: batchParam },
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        batchParam === "all"
          ? "Tally_GST_Rate_Setup_ALL_ENTRIES.xlsx"
          : `Tally_GST_Rate_Setup_Batch_${batchParam}.xlsx`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification("Tally Excel downloaded successfully!");
    } catch (err) {
      console.error("Export Excel error:", err);
      showNotification("Failed to export Excel file", "error");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const downloadTallyXml = async (batchParam = "all") => {
    try {
      setDownloadingXml(true);
      showNotification("Generating Tally XML file... please wait", "info");
      const res = await api.get("/hsn/export-tally-xml", {
        params: { batch: batchParam },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/xml" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        batchParam === "all"
          ? "Tally_Import_ALL_ENTRIES.xml"
          : `Tally_Import_Batch_${batchParam}.xml`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification("Tally XML downloaded successfully!");
    } catch (err) {
      console.error("Export XML error:", err);
      showNotification("Failed to export XML file", "error");
    } finally {
      setDownloadingXml(false);
    }
  };

  const summary = stats?.summary || {};
  const batches = stats?.batches || [];
  const aiQueueCount = Number(summary.ai_queue_count || 0);

  const pageItemIds = items
    .filter((it) => it.status !== "SECTION_HEADER")
    .map((it) => it.id);
  const isAllPageSelected =
    pageItemIds.length > 0 && pageItemIds.every((id) => selectedIds.includes(id));

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 sm:px-6 lg:px-8 pb-32">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-2xl text-white font-medium flex items-center gap-2 transition-all ${
            notification.type === "error"
              ? "bg-red-600"
              : notification.type === "info"
              ? "bg-blue-600"
              : "bg-emerald-600"
          }`}
        >
          <i
            className={`fas ${
              notification.type === "error"
                ? "fa-exclamation-circle"
                : notification.type === "info"
                ? "fa-info-circle"
                : "fa-check-circle"
            }`}
          ></i>
          <span>{notification.msg}</span>
        </div>
      )}

      {/* Header Bar */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center text-xl shadow-md">
                <i className="fas fa-barcode"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  HSN Master & Tally Synchronizer
                </h1>
                <p className="text-sm text-slate-500">
                  Official GST Portal 8-Digit Directory (21,935 Codes) • 500 Daily Review Batching • Deferred AI Queue
                </p>
              </div>
            </div>
          </div>

          {/* Master Export Actions */}
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              onClick={() => downloadTallyExcel("all")}
              disabled={downloadingExcel}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-xl shadow-sm hover:shadow transition-all flex items-center gap-2 text-sm cursor-pointer"
              title="Download entire 4,185 items in Tally Prime GST Rate Setup format"
            >
              {downloadingExcel ? (
                <>
                  <i className="fas fa-spinner fa-spin text-base"></i>
                  <span>Generating Excel...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-file-excel text-base"></i>
                  <span>Export ALL Entries to Tally (Excel)</span>
                </>
              )}
            </button>

            <button
              onClick={() => downloadTallyXml("all")}
              disabled={downloadingXml}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-xl shadow-sm hover:shadow transition-all flex items-center gap-2 text-sm cursor-pointer"
              title="Download entire catalog as direct Tally XML file"
            >
              {downloadingXml ? (
                <>
                  <i className="fas fa-spinner fa-spin text-base"></i>
                  <span>Generating XML...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-code text-base"></i>
                  <span>Export ALL (XML)</span>
                </>
              )}
            </button>

            <button
              onClick={() => {
                fetchStats();
                fetchItems();
              }}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-3.5 py-2.5 rounded-xl transition text-sm cursor-pointer"
              title="Refresh dataset"
            >
              <i className="fas fa-sync-alt"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="max-w-7xl mx-auto mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Total Items
          </div>
          <div className="text-2xl font-black text-slate-800 mt-1">
            {summary.total_items || 4185}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">Across 9 batches</div>
        </div>

        <div className="bg-emerald-50/70 p-4 rounded-xl border border-emerald-200 shadow-sm">
          <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
            Verified Valid
          </div>
          <div className="text-2xl font-black text-emerald-800 mt-1">
            {summary.verified_count || 0}
          </div>
          <div className="text-xs text-emerald-600 mt-0.5">
            Exact GST Master Match
          </div>
        </div>

        <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-200 shadow-sm">
          <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
            Auto-Matched
          </div>
          <div className="text-2xl font-black text-blue-800 mt-1">
            {summary.matched_count || 0}
          </div>
          <div className="text-xs text-blue-600 mt-0.5">
            Domain & 8-Digit Rules
          </div>
        </div>

        <div className="bg-amber-50/70 p-4 rounded-xl border border-amber-200 shadow-sm">
          <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
            Needs Review
          </div>
          <div className="text-2xl font-black text-amber-800 mt-1">
            {summary.needs_review_count || 0}
          </div>
          <div className="text-xs text-amber-600 mt-0.5">
            Pending manual approval
          </div>
        </div>

        {/* AI Queue Card */}
        <div
          onClick={() => {
            setActiveStatus("IN_QUEUE");
            setPage(1);
          }}
          className={`p-4 rounded-xl border shadow-sm cursor-pointer transition ${
            aiQueueCount > 0
              ? "bg-purple-50 border-purple-300 hover:bg-purple-100/80"
              : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className="text-xs font-semibold text-purple-700 uppercase tracking-wider flex items-center justify-between">
            <span>AI Review Queue</span>
            {aiQueueCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-purple-600 animate-ping"></span>
            )}
          </div>
          <div className="text-2xl font-black text-purple-900 mt-1">
            {aiQueueCount}
          </div>
          <div className="text-xs text-purple-600 mt-0.5 font-medium">
            {aiQueueCount > 0 ? "Click to view queue →" : "No items queued"}
          </div>
        </div>

        <div className="bg-purple-50/70 p-4 rounded-xl border border-purple-200 shadow-sm">
          <div className="text-xs font-semibold text-purple-700 uppercase tracking-wider">
            Approved
          </div>
          <div className="text-2xl font-black text-purple-800 mt-1">
            {summary.approved_count || 0}
          </div>
          <div className="text-xs text-purple-600 mt-0.5">
            Ready for invoicing
          </div>
        </div>
      </div>

      {/* AI Queue Action Banner */}
      {(aiQueueCount > 0 || activeStatus === "IN_QUEUE") && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white rounded-2xl shadow-lg p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-purple-700/50">
            <div className="flex items-start gap-3.5">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 border border-purple-400/40 text-purple-300 flex items-center justify-center text-2xl flex-shrink-0">
                <i className="fas fa-robot"></i>
              </div>
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <span>Deferred AI Review Queue</span>
                  <span className="bg-purple-500 text-white text-xs px-2.5 py-0.5 rounded-full font-extrabold">
                    {aiQueueCount} Items
                  </span>
                </h3>
                <p className="text-xs text-purple-200/90 mt-1 max-w-2xl leading-relaxed">
                  Items you queued with custom descriptions are held here. AI will analyze your notes and suggest exact official 8-digit GST HSN codes. <strong>Nothing will be auto-approved</strong> — you retain 100% control to inspect and approve every suggestion.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto justify-end">
              <button
                onClick={handleRunAiQueue}
                disabled={isProcessingQueue || aiQueueCount === 0}
                className="bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition flex items-center gap-2 text-xs cursor-pointer"
              >
                {isProcessingQueue ? (
                  <>
                    <i className="fas fa-spinner fa-spin text-sm"></i>
                    <span>AI Review in Progress...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic text-sm"></i>
                    <span>Run AI Review on Queued Items ({aiQueueCount})</span>
                  </>
                )}
              </button>

              {aiQueueCount > 0 && (
                <button
                  onClick={handleClearEntireQueue}
                  className="bg-white/10 hover:bg-white/20 text-purple-200 hover:text-white px-3.5 py-2.5 rounded-xl transition text-xs font-semibold cursor-pointer"
                  title="Remove all items from AI queue"
                >
                  Clear Queue
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 500 Daily Review Batch Selector */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <i className="fas fa-calendar-day text-blue-600"></i>
              <span>Select Daily Review Batch (500 Items / Day)</span>
            </h2>
            <button
              onClick={() => handleBulkApprove(activeBatch !== "all")}
              className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg shadow-sm transition flex items-center gap-1.5 cursor-pointer"
            >
              <i className="fas fa-check-double"></i>
              <span>
                {activeBatch === "all"
                  ? "Bulk Approve All Verified (System)"
                  : `Bulk Approve Day ${activeBatch} Verified`}
              </span>
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setActiveBatch("all");
                setPage(1);
              }}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                activeBatch === "all"
                  ? "bg-slate-900 text-white shadow"
                  : "bg-slate-100 hover:bg-slate-200 text-slate-700"
              }`}
            >
              <span>All Items</span>
              <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px]">
                {summary.total_items || 4185}
              </span>
            </button>

            {batches.map((b) => {
              const isActive = String(activeBatch) === String(b.batch_no);
              const needsReview = Number(b.needs_review) || 0;
              return (
                <button
                  key={b.batch_no}
                  onClick={() => {
                    setActiveBatch(String(b.batch_no));
                    setPage(1);
                  }}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                    isActive
                      ? "bg-blue-600 text-white shadow"
                      : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                  }`}
                >
                  <span>Day {b.batch_no}</span>
                  <span className="text-[10px] opacity-80">({b.total})</span>
                  {needsReview > 0 ? (
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        isActive
                          ? "bg-amber-400 text-slate-900 font-black"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {needsReview} rev
                    </span>
                  ) : (
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        isActive
                          ? "bg-emerald-400 text-slate-900 font-black"
                          : "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      ✓ 100%
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <span className="text-xs font-semibold text-slate-500">Filter:</span>
            {[
              { id: "all", label: "All Items" },
              { id: "NEEDS_REVIEW", label: "Needs Review" },
              {
                id: "IN_QUEUE",
                label: `⏳ AI Queue (${aiQueueCount})`,
                highlight: aiQueueCount > 0,
              },
              { id: "VERIFIED_VALID", label: "Verified" },
              { id: "MATCHED_FROM_MASTER", label: "Auto-Matched" },
              { id: "APPROVED", label: "Approved" },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => {
                  setActiveStatus(st.id);
                  setPage(1);
                }}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition cursor-pointer flex items-center gap-1.5 ${
                  activeStatus === st.id
                    ? "bg-slate-800 text-white shadow-sm"
                    : st.highlight
                    ? "bg-purple-100 text-purple-800 font-bold hover:bg-purple-200 border border-purple-300"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {st.label}
              </button>
            ))}

            {aiQueueCount > 0 && (
              <button
                type="button"
                onClick={handleRunAiQueue}
                disabled={isProcessingQueue}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 shadow-sm transition cursor-pointer disabled:opacity-50 ml-2"
                title="Execute AI Review on all queued items"
              >
                {isProcessingQueue ? (
                  <>
                    <i className="fas fa-spinner fa-spin text-xs"></i>
                    <span>AI Reviewing...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic text-xs"></i>
                    <span>Run AI on Queued ({aiQueueCount})</span>
                  </>
                )}
              </button>
            )}
          </div>

          <form
            onSubmit={handleSearchSubmit}
            className="flex items-center gap-2 w-full md:w-80"
          >
            <div className="relative w-full">
              <i className="fas fa-search absolute left-3 top-3 text-slate-400 text-xs"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search item, code, bearing, pump..."
                className="w-full pl-8 pr-3 py-2 text-xs border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="bg-blue-600 text-white text-xs px-3.5 py-2 rounded-xl font-medium hover:bg-blue-700"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Main Items Table */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Prominent Banner directly above table when in AI Queue */}
          {activeStatus === "IN_QUEUE" && (
            <div className="p-4 bg-gradient-to-r from-purple-50 via-indigo-50 to-slate-50 border-b border-purple-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center text-lg shadow-sm">
                  <i className="fas fa-robot"></i>
                </div>
                <div>
                  <div className="text-xs font-bold text-purple-950">
                    {items.length} Items Waiting in AI Review Queue
                  </div>
                  <div className="text-[11px] text-purple-700">
                    Click "Run AI Review Now" to analyze these items with your custom descriptions.
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleRunAiQueue}
                  disabled={isProcessingQueue || items.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2 shadow-md hover:shadow-lg transition cursor-pointer"
                >
                  {isProcessingQueue ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>AI is Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic"></i>
                      <span>Run AI Review Now ({items.length})</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleClearEntireQueue}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs px-3 py-2 rounded-xl font-medium transition cursor-pointer"
                >
                  Clear Queue
                </button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100/80 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                  <th className="py-3 px-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={isAllPageSelected}
                      onChange={handleToggleSelectAllOnPage}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                      title="Select / Deselect all items on this page"
                    />
                  </th>
                  <th className="py-3 px-2 w-10 text-center">#</th>
                  <th className="py-3 px-4 min-w-[240px]">Item Particulars & AI Hint</th>
                  <th className="py-3 px-3 w-28 text-center">Original HSN</th>
                  <th className="py-3 px-3 min-w-[190px]">8-Digit GST HSN Code</th>
                  <th className="py-3 px-3 w-24 text-center">GST Rate</th>
                  <th className="py-3 px-4 min-w-[220px]">Official Description</th>
                  <th className="py-3 px-3 w-28 text-center">Status</th>
                  <th className="py-3 px-3 w-32 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-12 text-slate-500">
                      <i className="fas fa-spinner fa-spin text-2xl mb-2 text-blue-600 block"></i>
                      Loading items...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-12 text-slate-400">
                      No items found matching the current filters.
                    </td>
                  </tr>
                ) : (
                  items.map((it, idx) => {
                    const rowNum = (page - 1) * limit + idx + 1;
                    const isApproved = it.is_approved === 1 || it.status === "APPROVED";
                    const isHeader = it.status === "SECTION_HEADER";
                    const isSelected = selectedIds.includes(it.id);
                    const isInQueue = it.in_ai_queue === 1;

                    if (isHeader) {
                      return (
                        <tr key={it.id} className="bg-slate-200 font-bold">
                          <td colSpan="9" className="py-2.5 px-4 text-slate-700">
                            📁 {it.particulars}
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr
                        key={it.id}
                        className={`hover:bg-slate-50/80 transition-colors ${
                          isSelected
                            ? "bg-blue-50/50"
                            : isInQueue
                            ? "bg-purple-50/30"
                            : isApproved
                            ? "bg-emerald-50/20"
                            : ""
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="py-3 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(it.id)}
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                          />
                        </td>

                        {/* Number */}
                        <td className="py-3 px-2 text-center text-slate-400 font-mono text-[11px]">
                          {rowNum}
                        </td>

                        {/* Particulars & AI Description Hint */}
                        <td className="py-3 px-4">
                          <div className="font-semibold text-slate-800">
                            {it.particulars}
                          </div>

                          {/* AI Hint / Context */}
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            {it.ai_hint ? (
                              <span
                                onClick={() => openHintModal(it)}
                                className="inline-flex items-center gap-1 text-[11px] text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded px-2 py-0.5 cursor-pointer transition font-medium"
                                title="Click to edit custom description for AI"
                              >
                                <i className="fas fa-lightbulb text-amber-500 text-[10px]"></i>
                                <span>Hint: {it.ai_hint}</span>
                                <i className="fas fa-pencil-alt text-[9px] text-indigo-400 ml-0.5"></i>
                              </span>
                            ) : (
                              <button
                                onClick={() => openHintModal(it)}
                                className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-indigo-600 cursor-pointer font-medium"
                                title="Add description / material context to help AI"
                              >
                                <i className="fas fa-plus-circle text-[10px]"></i>
                                <span>Add hint for AI</span>
                              </button>
                            )}

                            {/* In-Queue Badge */}
                            {isInQueue && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-700 bg-purple-100 border border-purple-300 rounded px-1.5 py-0.5 shadow-xs">
                                <i className="fas fa-clock text-[9px]"></i>
                                <span>In AI Queue</span>
                                <button
                                  onClick={() => handleDequeue([it.id])}
                                  className="hover:text-red-600 ml-1 text-[11px] font-black cursor-pointer"
                                  title="Remove from queue"
                                >
                                  ✕
                                </button>
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Original HSN */}
                        <td className="py-3 px-3 text-center font-mono">
                          {it.original_hsn ? (
                            it.original_hsn !== it.suggested_hsn ? (
                              <span
                                className="bg-amber-100 text-amber-900 border border-amber-300 px-1.5 py-0.5 rounded text-[11px] font-bold"
                                title="Original code was modified"
                              >
                                {it.original_hsn}
                              </span>
                            ) : (
                              <span className="text-slate-600 font-medium">
                                {it.original_hsn}
                              </span>
                            )
                          ) : (
                            <span className="text-rose-400 italic text-[10px] bg-rose-50 px-1.5 py-0.5 rounded">
                              Missing
                            </span>
                          )}
                        </td>

                        {/* 8-Digit HSN Input with Live Autocomplete */}
                        <td className="py-2 px-3 relative">
                          <div className="relative">
                            <input
                              type="text"
                              value={it.suggested_hsn || ""}
                              onChange={(e) =>
                                handleHsnInputChange(it.id, e.target.value)
                              }
                              onFocus={() => setActiveSearchId(it.id)}
                              placeholder="Type HSN..."
                              className="w-full px-2.5 py-1.5 font-mono text-xs font-bold border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            />
                            {searchLoading && activeSearchId === it.id && (
                              <i className="fas fa-spinner fa-spin absolute right-2.5 top-2.5 text-blue-500 text-xs"></i>
                            )}
                          </div>

                          {/* Autocomplete Dropdown */}
                          {activeSearchId === it.id &&
                            masterSuggestions.length > 0 && (
                              <div className="absolute left-0 top-full mt-1 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 max-h-64 overflow-y-auto">
                                <div className="p-2 bg-slate-50 border-b text-[10px] font-bold text-slate-500 uppercase flex justify-between">
                                  <span>GST Portal Official Codes</span>
                                  <button
                                    onClick={() => setActiveSearchId(null)}
                                    className="text-slate-400 hover:text-slate-600 cursor-pointer"
                                  >
                                    ✕
                                  </button>
                                </div>
                                {masterSuggestions.map((sug) => (
                                  <div
                                    key={sug.hsn_code}
                                    onClick={() =>
                                      handleSelectSuggestion(it.id, sug)
                                    }
                                    className="p-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="font-mono font-bold text-blue-700 text-xs">
                                        {sug.hsn_code}
                                      </span>
                                      <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 rounded">
                                        {sug.type}
                                      </span>
                                    </div>
                                    <div className="text-[11px] text-slate-600 line-clamp-1 mt-0.5">
                                      {sug.hsn_description}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                        </td>

                        {/* GST Rate */}
                        <td className="py-3 px-3 text-center">
                          <select
                            value={it.gst_rate || "18%"}
                            onChange={(e) =>
                              handleRateChange(it.id, e.target.value)
                            }
                            className="text-xs font-semibold py-1 px-1.5 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                          >
                            {GST_RATES.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Official Description */}
                        <td className="py-3 px-4 text-slate-600 text-[11px] leading-relaxed">
                          {it.suggested_desc || (
                            <span className="text-slate-300 italic">
                              Pending matching
                            </span>
                          )}
                        </td>

                        {/* Status Badge */}
                        <td className="py-3 px-3 text-center">
                          {isApproved ? (
                            <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                              <i className="fas fa-check-circle"></i> Approved
                            </span>
                          ) : it.status === "VERIFIED_VALID" ? (
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                              <i className="fas fa-shield-alt"></i> Verified
                            </span>
                          ) : it.status === "MATCHED_FROM_MASTER" ? (
                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                              <i className="fas fa-link"></i> Matched
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                              <i className="fas fa-exclamation-triangle"></i> Review
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {/* Approve / Undo */}
                            <button
                              onClick={() => handleToggleApprove(it.id, isApproved)}
                              title={isApproved ? "Approved - Click to undo" : "Click to approve"}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition shadow-sm cursor-pointer ${
                                isApproved
                                  ? "bg-purple-100 hover:bg-purple-200 text-purple-700 border border-purple-300"
                                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
                              }`}
                            >
                              <i className={`fas ${isApproved ? "fa-check-double text-xs" : "fa-check text-xs"}`}></i>
                            </button>

                            {/* Add Hint / Queue for AI */}
                            <button
                              onClick={() => openHintModal(it)}
                              title="Set description hint / queue for AI"
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition border cursor-pointer ${
                                isInQueue
                                  ? "bg-purple-100 text-purple-700 border-purple-300"
                                  : "bg-slate-100 hover:bg-purple-50 text-slate-600 hover:text-purple-600 border-slate-200"
                              }`}
                            >
                              <i className="fas fa-edit text-xs"></i>
                            </button>

                            {/* Instant 1-click AI Assist */}
                            <button
                              onClick={() => handleAskAi(it.id, it.ai_hint)}
                              disabled={aiLoadingId === it.id}
                              title="Ask AI immediately using description hint"
                              className="w-7 h-7 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 flex items-center justify-center transition border border-indigo-200 cursor-pointer disabled:opacity-50"
                            >
                              {aiLoadingId === it.id ? (
                                <i className="fas fa-spinner fa-spin text-xs"></i>
                              ) : (
                                <i className="fas fa-magic text-xs"></i>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="p-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing {(page - 1) * limit + 1} to{" "}
              {Math.min(page * limit, pagination.total)} of {pagination.total} items
            </div>

            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="text-xs border border-slate-300 rounded-lg px-2 py-1 bg-white focus:outline-none cursor-pointer"
              >
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={250}>250 per page</option>
                <option value={500}>500 per page</option>
              </select>

              <div className="flex items-center gap-1">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  className="px-2.5 py-1 text-xs border rounded-lg bg-white disabled:opacity-40 hover:bg-slate-100 cursor-pointer"
                >
                  Prev
                </button>
                <span className="text-xs font-semibold px-2 text-slate-700">
                  {page} / {pagination.totalPages || 1}
                </span>
                <button
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                  className="px-2.5 py-1 text-xs border rounded-lg bg-white disabled:opacity-40 hover:bg-slate-100 cursor-pointer"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Bulk Action Bar (Visible when 1 or more items selected) */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 bg-slate-900/95 backdrop-blur-md text-white border border-slate-700 shadow-2xl rounded-2xl px-5 py-3.5 flex flex-wrap items-center gap-4 transition-all">
          <div className="flex items-center gap-2 border-r border-slate-700 pr-3">
            <span className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold">
              {selectedIds.length}
            </span>
            <span className="text-xs font-semibold">Selected</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Add to AI Queue */}
            <button
              onClick={handleQueueSelected}
              className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Add selected items to AI review queue"
            >
              <i className="fas fa-clock"></i>
              <span>Queue for AI ({selectedIds.length})</span>
            </button>

            {/* Set Common Description Hint */}
            <button
              onClick={() => {
                setBulkHintText("");
                setBulkHintModalOpen(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Add a custom description to all selected items & queue for AI"
            >
              <i className="fas fa-pencil-alt"></i>
              <span>Add Hint & Queue</span>
            </button>

            {/* Bulk Approve Selected */}
            <button
              onClick={handleBulkApproveSelected}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Mark selected items as Approved"
            >
              <i className="fas fa-check-double"></i>
              <span>Approve Selected</span>
            </button>

            {/* Dequeue Selected */}
            <button
              onClick={() => handleDequeue(selectedIds)}
              className="bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs px-2.5 py-1.5 rounded-xl transition cursor-pointer"
              title="Remove selected from AI queue"
            >
              Dequeue
            </button>
          </div>

          <button
            onClick={handleClearSelection}
            className="text-slate-400 hover:text-white text-xs font-bold pl-2 cursor-pointer"
            title="Deselect all"
          >
            ✕
          </button>
        </div>
      )}

      {/* Single Item Hint Modal */}
      {hintModalItem && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-700 to-purple-800 text-white p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-lg">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold">AI Description Context</h3>
                    <p className="text-xs text-indigo-200">
                      Help AI pick the exact 8-digit HSN code
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeHintModal}
                  className="text-white/70 hover:text-white text-xl cursor-pointer"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 tracking-wider">
                  Item Particulars
                </label>
                <div className="mt-1 text-sm font-bold text-slate-800 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                  {hintModalItem.particulars}
                </div>
                <div className="mt-1 text-xs text-slate-500 flex gap-4">
                  <span>
                    Raw HSN: <strong>{hintModalItem.original_hsn || "None"}</strong>
                  </span>
                  <span>
                    Current Suggestion: <strong>{hintModalItem.suggested_hsn || "Pending"}</strong>
                  </span>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 tracking-wider flex items-center justify-between">
                  <span>Custom Description / Material / Use</span>
                  <span className="text-indigo-600 font-normal lowercase text-xs">
                    (passed directly to AI)
                  </span>
                </label>
                <textarea
                  rows="3"
                  value={hintText}
                  onChange={(e) => setHintText(e.target.value)}
                  placeholder="e.g. Teflon washer for submersible pump, Stainless Steel 304 bolt, Cast iron impeller..."
                  className="mt-1 w-full p-3 text-xs border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                ></textarea>
              </div>

              {/* Quick Suggestion Tags */}
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Quick Classification Presets
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {COMMON_HINTS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() =>
                        setHintText((prev) => (prev ? `${prev}, ${h}` : h))
                      }
                      className="text-[10px] bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 px-2.5 py-1 rounded-lg transition border border-slate-200 cursor-pointer"
                    >
                      + {h}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 p-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleSaveHintOnly}
                className="text-xs font-semibold text-slate-700 hover:bg-slate-200 px-3 py-2 rounded-xl transition cursor-pointer"
              >
                Save Hint Only
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSaveHintAndQueue}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
                  title="Save hint and add to queue. Review when you run the batch!"
                >
                  <i className="fas fa-clock"></i>
                  <span>Queue for AI Review</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    closeHintModal();
                    handleAskAi(hintModalItem.id, hintText);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
                  title="Ask AI right now with this description"
                >
                  <i className="fas fa-magic"></i>
                  <span>Review Now</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Hint Modal (For Multiple Items) */}
      {bulkHintModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-800 to-indigo-900 text-white p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-lg">
                    <i className="fas fa-layer-group"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold">
                      Add Description & Queue ({selectedIds.length} Items)
                    </h3>
                    <p className="text-xs text-purple-200">
                      Apply a common description to all selected items
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setBulkHintModalOpen(false)}
                  className="text-white/70 hover:text-white text-xl cursor-pointer"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-500 tracking-wider">
                  Common Description Hint for Selected Items
                </label>
                <textarea
                  rows="3"
                  value={bulkHintText}
                  onChange={(e) => setBulkHintText(e.target.value)}
                  placeholder="e.g. Submersible pump spares, cast iron parts, bearings, mechanical seals..."
                  className="mt-1 w-full p-3 text-xs border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                ></textarea>
              </div>

              {/* Presets */}
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Quick Classification Presets
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {COMMON_HINTS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() =>
                        setBulkHintText((prev) => (prev ? `${prev}, ${h}` : h))
                      }
                      className="text-[10px] bg-slate-100 hover:bg-purple-50 hover:text-purple-700 text-slate-600 px-2.5 py-1 rounded-lg transition border border-slate-200 cursor-pointer"
                    >
                      + {h}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 p-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setBulkHintModalOpen(false)}
                className="text-xs font-semibold text-slate-600 hover:bg-slate-200 px-3 py-2 rounded-xl transition cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleApplyBulkHintAndQueue}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <i className="fas fa-check"></i>
                <span>Queue {selectedIds.length} Items for AI Review</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
